/**
 * User Role Management Modal - Extracted from admin.tsx per ADR-039
 * Modal for assigning and removing roles from users
 */

import type {
  RoleResponse,
  UserRoleResponse,
  UserWithRoles,
} from "@/types/models"
import {
  Badge,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Flex,
  Portal,
  Stack,
  Text,
} from "@chakra-ui/react"

import { Button } from "@/components/atoms/Button"

export function UserRoleManagementModal({
  user,
  availableRoles,
  onRoleAssign,
  onRoleRemove,
  onClose,
}: {
  user: UserWithRoles
  availableRoles: RoleResponse[]
  onRoleAssign: (userId: string, roleId: string) => void
  onRoleRemove: (userId: string, roleId: string) => void
  onClose: () => void
}) {
  const assignedRoleIds = new Set(
    Array.isArray(user.roles)
      ? user.roles.map((ur: UserRoleResponse) => ur.role.id)
      : [],
  )

  return (
    <DialogRoot
      open={true}
      onOpenChange={(e) => !e.open && onClose()}
      size={{ base: "full", md: "xl" }}
    >
      <Portal>
        <DialogBackdrop />
        <DialogPositioner
          inset="0"
          display="grid"
          placeItems="center"
          padding={{ base: 4, md: 6 }}
        >
          <DialogContent maxH="90vh" display="flex" flexDirection="column">
            <DialogHeader>
              <DialogTitle>Manage Roles for {user.full_name}</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>
            <DialogBody pb={6} overflowY="auto" flex="1">
              <Stack gap={6} align="stretch">
                {/* Current Roles */}
                <Stack align="stretch" gap={3}>
                  <Text fontWeight="medium">Current Roles</Text>
                  {Array.isArray(user.roles) && user.roles.length > 0 ? (
                    user.roles.map((userRole: UserRoleResponse) => (
                      <Flex
                        key={userRole.role.id}
                        justify="space-between"
                        align="center"
                        p={3}
                        border="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                      >
                        <Stack align="start" gap={1}>
                          <Badge
                            colorScheme={
                              userRole.role.is_system_role ? "blue" : "purple"
                            }
                          >
                            {userRole.role.display_name || userRole.role.name}
                          </Badge>
                          <Text fontSize="sm" color="gray.600">
                            {userRole.role.description}
                          </Text>
                        </Stack>
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          onClick={() =>
                            onRoleRemove(user.id, userRole.role.id)
                          }
                        >
                          Remove
                        </Button>
                      </Flex>
                    ))
                  ) : (
                    <Text color="gray.500">No roles assigned</Text>
                  )}
                </Stack>

                {/* Available Roles */}
                <Stack align="stretch" gap={3}>
                  <Text fontWeight="medium">Available Roles</Text>
                  {availableRoles
                    .filter(
                      (role: RoleResponse) => !assignedRoleIds.has(role.id),
                    )
                    .map((role: RoleResponse) => (
                      <Flex
                        key={role.id}
                        justify="space-between"
                        align="center"
                        p={3}
                        border="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                      >
                        <Stack align="start" gap={1}>
                          <Badge
                            colorScheme={
                              role.is_system_role ? "blue" : "purple"
                            }
                          >
                            {role.display_name || role.name}
                          </Badge>
                          <Text fontSize="sm" color="gray.600">
                            {role.description}
                          </Text>
                        </Stack>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          onClick={() => onRoleAssign(user.id, role.id)}
                        >
                          Assign
                        </Button>
                      </Flex>
                    ))}
                </Stack>
              </Stack>
            </DialogBody>
          </DialogContent>
        </DialogPositioner>
      </Portal>
    </DialogRoot>
  )
}
